import { MINIMUM_SEARCH_TERM_LENGTH, useProductSearchQuery } from "@fero/hooks";
import type { AvailableStock } from "@fero/pages";
import type { SelectedProduct } from "@fero/stores";
import CloseIcon from "@mui/icons-material/Close";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import {
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Modal,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import type { NumberOfUnits, ProductId } from "adl-gen/ferovinum/app/db";
import React, { useState } from "react";
import { useGetStock } from "../../hooks/use-get-stock";
import { type useManageBrowseProducts, useTradeSaleHelpers } from "../hooks";
import { BrowseProductsModalTable } from "./browse-products-modal-table";

interface BrowseProductsModalProps {
  manageBrowseProducts: ReturnType<typeof useManageBrowseProducts>;
  onAddProducts: (selectedProducts: SelectedProduct[]) => void;
  producibles: Map<ProductId, string>;
}

/**
 * Utility method to get the product ids to show in the browse products modal
 * - If onlyShowSelected is true, filter down search results or show all selected
 * - If there are search results, then show them
 * - Otherwise show all browsable products
 */
const getProductsToShow = (
  onlyShowSelected: boolean,
  aggregatedStock: AvailableStock,
  selectedProducts: string[],
  searchTerm: string,
  searchResults: string[],
): AvailableStock => {
  const hasValidSearchTerm = searchTerm.length >= MINIMUM_SEARCH_TERM_LENGTH;

  // If no filtering conditions, return the full stock
  if (!onlyShowSelected && !hasValidSearchTerm && searchResults.length === 0) {
    return aggregatedStock;
  }

  const filteredStock = new Map<ProductId, NumberOfUnits>();

  for (const [productId, stockItem] of aggregatedStock.entries()) {
    const isSelectedProduct =
      !onlyShowSelected || selectedProducts.includes(productId);
    const isSearchMatch =
      !hasValidSearchTerm || searchResults.includes(productId);

    if (isSelectedProduct && isSearchMatch) {
      filteredStock.set(productId, stockItem);
    }
  }

  return filteredStock;
};

export const BrowseProductsModal: React.FC<BrowseProductsModalProps> = ({
  onAddProducts,
  manageBrowseProducts,
  producibles,
}) => {
  const { storageLocationId, nominatedPurchaser } = useTradeSaleHelpers();
  const { isBrowseOpen, browsingProducts, closeBrowse, clearBrowsingProducts } =
    manageBrowseProducts;
  const [onlyShowSelected, setOnlyShowSelected] = React.useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const {
    aggregatedStock,
    isLoading: isAvailableStockError,
    isError: isAvailableStockLoading,
  } = useGetStock(nominatedPurchaser.organisationId, storageLocationId);

  const { data: searchResults = [], isFetching: isSearchLoading } =
    useProductSearchQuery(searchTerm);
  if (isAvailableStockLoading || isAvailableStockError) {
    return <></>;
  }

  const onAdd = () => {
    // Let the parent know that the products have been added and then clear the selected products
    onAddProducts(browsingProducts);
    closeBrowse();
    clearBrowsingProducts();
  };

  const onBack = () => {
    // Let the parent know that the products have been added and then clear the selected products
    closeBrowse();
    clearBrowsingProducts();
  };

  const productsToShow = getProductsToShow(
    onlyShowSelected,
    aggregatedStock,
    browsingProducts.map((p) => p.productId),
    searchTerm,
    searchResults.map((p) => p.id),
  );

  return (
    <Modal
      open={isBrowseOpen}
      onClose={closeBrowse}
      aria-labelledby="parent-modal-title"
      aria-describedby="parent-modal-description"
    >
      <Stack
        justifyContent={"space-between"}
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "80vw",
          height: "90vh",
          backgroundColor: (theme) => theme.palette.background.paper,
          borderRadius: 1,
          padding: 4,
        }}
      >
        <Stack flex={1} display={"flex"} spacing={4}>
          <Stack direction={"row"} justifyContent={"space-between"}>
            <Typography variant="h5">
              {onlyShowSelected ? "Selected" : "Select"} Products
            </Typography>
            <IconButton onClick={onBack}>
              <CloseIcon />
            </IconButton>
          </Stack>
          <TextField
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlinedIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <>
                  {isSearchLoading && (
                    <CircularProgress color="inherit" size={20} />
                  )}
                </>
              ),
            }}
            variant="outlined"
            fullWidth
            label="Search Products"
            autoComplete="productSearch"
            autoFocus
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <BrowseProductsModalTable
            productsToShow={productsToShow}
            producibles={producibles}
          />
        </Stack>

        <Stack
          direction="row"
          justifyContent={"space-between"}
          justifySelf={"flex-end"}
        >
          <Chip
            label={`${browsingProducts.length} / ${productsToShow.size} Products Selected`}
            variant={onlyShowSelected ? "filled" : "outlined"}
            clickable
            onClick={() => setOnlyShowSelected(!onlyShowSelected)}
          />
          <Stack direction="row" spacing={2}>
            <Button variant="outlined" onClick={onBack}>
              Back
            </Button>
            <Button onClick={onAdd}>Add</Button>
          </Stack>
        </Stack>
      </Stack>
    </Modal>
  );
};
